import get from 'lodash/get';
import set from 'lodash/set';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateComponent } from '~/app/components/dynamic-grid/dynamic-grid-helpers';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';

const getInitialState = (
  currentComponent: any,
  componentPath: string,
  field: string,
  initialValue: any
) => {
  return get(currentComponent, `props.${componentPath}.${field}`, initialValue);
};

type UseDynamicThirdPartyEmbedProps = {
  fieldPath?: string;
};

export const useDynamicThirdPartyEmbed = ({
  fieldPath = '',
}: UseDynamicThirdPartyEmbedProps = {}) => {
  const dispatch = useDispatch();
  const { componentPath } = useDynamicGrid();
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const currentComponent = flatOutline?.[selectedComponentId];

  const [embedType, setEmbedType] = useState<string>('');
  const [iframeSrc, setIframeSrc] = useState<string>('');
  const [scriptSrc, setScriptSrc] = useState<string>('');
  const [cffId, setCffId] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [width, setWidth] = useState<string>('');
  const [tabletHeight, setTabletHeight] = useState<string>('');
  const [tabletWidth, setTabletWidth] = useState<string>('');
  const [mobileHeight, setMobileHeight] = useState<string>('');
  const [mobileWidth, setMobileWidth] = useState<string>('');
  const [customCss, setCustomCss] = useState<string>('');

  const handleUpdateComponent = useCallback(
    (field: string, value: string | number | any[]) => {
      const fieldPathValue = fieldPath
        ? `props.${componentPath}.${fieldPath}.${field}` // If fieldPath is provided, update the nested field
        : `props.${componentPath}.${field}`; // If fieldPath is not provided, update the top-level field
      set(currentComponent, fieldPathValue, value);
      dispatch(updateComponent(currentComponent));
    },
    [currentComponent, componentPath, fieldPath, dispatch]
  );

  const handleEmbedTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleUpdateComponent('embedType', e.target.value);
    setEmbedType(e.target.value);
  };

  const handleIframeSrcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('iframeSrc', e.target.value);
    setIframeSrc(e.target.value);
  };

  const handleScriptSrcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('scriptSrc', e.target.value);
    setScriptSrc(e.target.value);
  };

  const handleCffIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('cffId', e.target.value);
    setCffId(e.target.value);
  };

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('height', e.target.value);
    setHeight(e.target.value);
  };

  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('width', e.target.value);
    setWidth(e.target.value);
  };

  const handleTabletHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('tabletHeight', e.target.value);
    setTabletHeight(e.target.value);
  };

  const handleTabletWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('tabletWidth', e.target.value);
    setTabletWidth(e.target.value);
  };

  const handleMobileHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('mobileHeight', e.target.value);
    setMobileHeight(e.target.value);
  };

  const handleMobileWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('mobileWidth', e.target.value);
    setMobileWidth(e.target.value);
  };

  const handleCustomCssChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleUpdateComponent('customCss', e.target.value);
    setCustomCss(e.target.value);
  };

  useEffect(() => {
    setEmbedType(
      getInitialState(currentComponent, componentPath, 'embedType', '')
    );
    setIframeSrc(
      getInitialState(currentComponent, componentPath, 'iframeSrc', '')
    );
    setScriptSrc(
      getInitialState(currentComponent, componentPath, 'scriptSrc', '')
    );
    setCffId(getInitialState(currentComponent, componentPath, 'cffId', ''));
    setHeight(getInitialState(currentComponent, componentPath, 'height', ''));
    setWidth(getInitialState(currentComponent, componentPath, 'width', ''));
    setTabletHeight(
      getInitialState(currentComponent, componentPath, 'tabletHeight', '')
    );
    setTabletWidth(
      getInitialState(currentComponent, componentPath, 'tabletWidth', '')
    );
    setMobileHeight(
      getInitialState(currentComponent, componentPath, 'mobileHeight', '')
    );
    setMobileWidth(
      getInitialState(currentComponent, componentPath, 'mobileWidth', '')
    );
    setCustomCss(
      getInitialState(currentComponent, componentPath, 'customCss', '')
    );
  }, [
    currentComponent,
    componentPath,
    setEmbedType,
    setIframeSrc,
    setScriptSrc,
    setCffId,
    setHeight,
    setWidth,
    setTabletHeight,
    setTabletWidth,
    setMobileHeight,
    setMobileWidth,
    setCustomCss,
  ]);

  return {
    currentComponent,
    componentPath,
    embedType,
    iframeSrc,
    scriptSrc,
    cffId,
    height,
    width,
    tabletHeight,
    tabletWidth,
    mobileHeight,
    mobileWidth,
    customCss,
    handleEmbedTypeChange,
    handleIframeSrcChange,
    handleScriptSrcChange,
    handleCffIdChange,
    handleHeightChange,
    handleWidthChange,
    handleTabletHeightChange,
    handleTabletWidthChange,
    handleMobileHeightChange,
    handleMobileWidthChange,
    handleCustomCssChange,
  };
};
